/**
 * Hook para refresh inteligente - JurisPrazo V2
 * Evita recarregamentos desnecessários quando usuário volta à aba
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook que gerencia refresh inteligente baseado no foco da aba
 * @param {Function} refreshFunction - Função para recarregar dados
 * @param {Object} options - Opções de configuração
 * @returns {Object} - Controles do refresh
 */
export const useSmartRefresh = (refreshFunction, options = {}) => {
  const {
    // Tempo mínimo entre refreshes (em ms)
    minInterval = 30000, // 30 segundos
    // Tempo máximo sem refresh antes de forçar (em ms)  
    maxInterval = 300000, // 5 minutos
    // Se deve fazer refresh na primeira vez
    refreshOnMount = true,
    // Se deve fazer refresh quando volta à aba
    refreshOnFocus = true,
    // Nome do contexto para debug
    context = 'unknown'
  } = options;

  const lastRefreshTime = useRef(Date.now());
  const isFirstMount = useRef(true);
  const tabWasHidden = useRef(false);

  const shouldRefresh = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;

    // Se passou muito tempo, sempre refresh
    if (timeSinceLastRefresh > maxInterval) {
      console.log(`🔄 [${context}] Refresh forçado - muito tempo sem atualizar (${Math.round(timeSinceLastRefresh/1000)}s)`);
      return true;
    }

    // Se não passou o tempo mínimo, não refresh
    if (timeSinceLastRefresh < minInterval) {
      console.log(`⏳ [${context}] Refresh bloqueado - muito recente (${Math.round(timeSinceLastRefresh/1000)}s < ${minInterval/1000}s)`);
      return false;
    }

    // Se a aba estava oculta e voltou, refresh
    if (tabWasHidden.current && refreshOnFocus) {
      console.log(`👁️ [${context}] Refresh por foco - aba estava oculta`);
      return true;
    }

    return false;
  }, [minInterval, maxInterval, refreshOnFocus, context]);

  const executeRefresh = useCallback(async () => {
    if (shouldRefresh()) {
      try {
        lastRefreshTime.current = Date.now();
        tabWasHidden.current = false;
        await refreshFunction();
        console.log(`✅ [${context}] Refresh executado com sucesso`);
      } catch (error) {
        console.error(`❌ [${context}] Erro no refresh:`, error);
      }
    }
  }, [refreshFunction, shouldRefresh, context]);

  const forceRefresh = useCallback(async () => {
    try {
      lastRefreshTime.current = Date.now();
      tabWasHidden.current = false;
      await refreshFunction();
      console.log(`🔥 [${context}] Refresh forçado executado`);
    } catch (error) {
      console.error(`❌ [${context}] Erro no refresh forçado:`, error);
    }
  }, [refreshFunction, context]);

  // Effect para gerenciar visibilidade da aba
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabWasHidden.current = true;
        console.log(`🙈 [${context}] Aba ficou oculta`);
      } else if (tabWasHidden.current) {
        console.log(`👁️ [${context}] Aba voltou a ficar visível`);
        // Não executa imediatamente, deixa para o próximo foco
      }
    };

    const handleFocus = () => {
      if (tabWasHidden.current || document.hidden) {
        console.log(`🎯 [${context}] Foco detectado após aba oculta`);
        executeRefresh();
      }
    };

    const handleBlur = () => {
      tabWasHidden.current = true;
      console.log(`😴 [${context}] Aba perdeu foco`);
    };

    // Adicionar listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [executeRefresh, context]);

  // Effect para refresh inicial
  useEffect(() => {
    if (isFirstMount.current && refreshOnMount) {
      isFirstMount.current = false;
      console.log(`🚀 [${context}] Refresh inicial`);
      forceRefresh();
    }
  }, [forceRefresh, refreshOnMount, context]);

  return {
    // Função para refresh manual
    refresh: forceRefresh,
    // Função para refresh inteligente
    smartRefresh: executeRefresh,
    // Informações de estado
    lastRefreshTime: lastRefreshTime.current,
    // Função para verificar se deve fazer refresh
    shouldRefresh
  };
};

/**
 * Hook simplificado para componentes que só precisam de refresh básico
 */
export const useTabRefresh = (refreshFunction, context = 'component') => {
  return useSmartRefresh(refreshFunction, {
    minInterval: 30000, // 30 segundos
    maxInterval: 300000, // 5 minutos
    refreshOnMount: true,
    refreshOnFocus: true,
    context
  });
};

/**
 * Hook para refresh conservador (menos frequente)
 */
export const useConservativeRefresh = (refreshFunction, context = 'component') => {
  return useSmartRefresh(refreshFunction, {
    minInterval: 120000, // 2 minutos
    maxInterval: 600000, // 10 minutos
    refreshOnMount: true,
    refreshOnFocus: false, // Não refresh automático no foco
    context
  });
};

export default useSmartRefresh;
